import { ValidationError } from '@app/domain/entities';
import type { TableCell } from './statement-table-reader';
import { normalizeCell } from './statement-value-utils';

const DEFAULT_NO_ROWS_ERROR_MESSAGE = 'The file does not contain any rows';
const DEFAULT_NO_IMPORTABLE_ROWS_ERROR_MESSAGE = 'No importable rows were found in the selected file';
const DEFAULT_MISSING_HEADER_ERROR_MESSAGE = 'The selected file is missing required columns';

export interface ResolveHeaderRowOptions<HeaderContext> {
  resolveHeaderIndexes: (headerRow: TableCell[]) => HeaderContext;
  missingHeaderErrorMessage?: string;
}

export interface MapStatementRowsOptions<HeaderContext, Row> extends ResolveHeaderRowOptions<HeaderContext> {
  mapRow: (row: TableCell[], headerIndexes: HeaderContext, rowNumber: number) => Row | null;
  getRowKey: (row: Row) => string;
  noRowsErrorMessage?: string;
  noImportableRowsErrorMessage?: string;
}

export function mapStatementRows<HeaderContext, Row>(
  rows: TableCell[][],
  options: MapStatementRowsOptions<HeaderContext, Row>,
): Row[] {
  const nonEmptyRows = filterNonEmptyRows(rows);
  if (nonEmptyRows.length === 0) {
    throw new ValidationError('file', options.noRowsErrorMessage ?? DEFAULT_NO_ROWS_ERROR_MESSAGE);
  }

  const { headerIndexes, dataRows } = resolveHeaderRow(nonEmptyRows, options);
  const parsedRows: Row[] = [];

  dataRows.forEach((row, index) => {
    const parsed = options.mapRow(row, headerIndexes, index + 2);
    if (parsed) {
      parsedRows.push(parsed);
    }
  });

  if (parsedRows.length === 0) {
    throw new ValidationError(
      'file',
      options.noImportableRowsErrorMessage ?? DEFAULT_NO_IMPORTABLE_ROWS_ERROR_MESSAGE,
    );
  }

  return dedupeRows(parsedRows, options.getRowKey);
}

export function resolveHeaderRow<HeaderContext>(
  rows: TableCell[][],
  options: ResolveHeaderRowOptions<HeaderContext>,
): { headerIndexes: HeaderContext; dataRows: TableCell[][] } {
  let lastError: ValidationError | null = null;

  for (const [index, row] of rows.entries()) {
    try {
      return {
        headerIndexes: options.resolveHeaderIndexes(row),
        dataRows: rows.slice(index + 1),
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        lastError = error;
        continue;
      }
      throw error;
    }
  }

  throw lastError ?? new ValidationError('file', options.missingHeaderErrorMessage ?? DEFAULT_MISSING_HEADER_ERROR_MESSAGE);
}

export function filterNonEmptyRows(rows: TableCell[][]): TableCell[][] {
  return rows.filter((row) => row.some((cell) => normalizeCell(cell) !== ''));
}

export function dedupeRows<Row>(rows: Row[], getRowKey: (row: Row) => string): Row[] {
  return [...new Map(rows.map((row) => [getRowKey(row), row])).values()];
}
